import { engineKeyFromCountry, HOME_FEE_FALLBACK_USD } from "./countries";
import { filterScholarshipsByLevel } from "../utils/scholarshipLevel";
import { knownAnnualCost, partialAnnualReferenceNote } from "./feeYear";

// Degrees tab metrics (moved from pages/Compare.jsx unchanged so the cost logic is unit-testable).
export function degreeMetrics(row, format, feeStatusMap) {
  const d = row.degree || {};
  const u = row.university || {};
  const cost = d.cost || {};
  const durationYears = d.duration_years || 3;
  const isHesa = (d.id || d.degree_id || "").startsWith("uk-hesa-");
  const country = u.country || "—";

  // Fee-status integration: look up the student's inferred status for
  // this country via the engine map. When status == "home" we swap the
  // intl tuition out for the domestic rate (course-level if published,
  // otherwise the country-level fallback). Everything else — total
  // cost, net after top aid, scholarships — recomputes from the chosen
  // tuition so the whole cost column tells one consistent story.
  const engineKey = engineKeyFromCountry(country);
  const inference = engineKey && feeStatusMap ? feeStatusMap[engineKey] : null;
  const feeStatus = inference?.status === "home" ? "home" : "international";

  // Small helper: build a reason string for a blank cell so we can
  // render a "why?" tooltip instead of an unexplained em-dash.
  // Reasons must be true, honest, and country-aware.
  const REASON_NO_UPSTREAM = "No public dataset publishes this at course level in this country — we won't guess.";
  const REASON_HESA_SUPPRESSED = "HESA / DfE LEO suppresses cohorts under ~30 respondents for privacy — smaller programmes don't get a published median.";
  const REASON_HESA_NO_OFFER = "UCAS publishes offer rates only at course-provider aggregate. HESA Discover Uni doesn't publish per-course offer rates — we could estimate, but we won't.";
  const REASON_INSTITUTION_ONLY = "US Common Data Set publishes admission rate per institution, not per programme. Splitting it by subject would be a fabrication.";
  const REASON_UNI_POLICY = "This university applies a single English-language policy across all courses — check the uni's admissions page for the current threshold.";
  const REASON_NO_FEEDER_DATA = "We stamp employers only where there's verified feeder-cohort evidence. No verified feeders published for this programme.";
  const REASON_IB_NOT_PUBLISHED = "The IB equivalent isn't listed in this course's published entry requirements.";

  // Tuition fallback chain — necessary because HESA-derived UK courses
  // (source_tier="verified", id prefix "uk-hesa-") store native GBP on
  // the cost block and inherit intl USD from the parent university doc,
  // not the degree. Without this fallback the Compare table renders
  // $0.00 for Birmingham / Nottingham / etc. even though the DegreeDetail
  // page (which already knows this trick) shows the correct total.
  const GBP_USD = 1.27;  // rough spot; UI shows USD anyway, currency-aware components handle live FX elsewhere.
  // Null-aware selection: an explicit finite 0 is a real value; GBP converts only when a GBP input is the one selected.
  const usd = (v) => (Number.isFinite(v) ? v : null);
  const gbp = (v) => (Number.isFinite(v) ? Math.round(v * GBP_USD) : null);
  const pick = (cands) => cands.find((x) => x.v != null) || { v: null, level: null };
  const intlPick = pick([
    { v: usd(cost.tuition_intl_annual_usd), level: "course" },
    { v: gbp(cost.tuition_intl_annual_gbp), level: "course" },
    { v: usd(u.tuition_intl_annual_usd), level: "uni" },
    { v: gbp(u.tuition_intl_annual_gbp), level: "uni" },
    { v: gbp(u.tuition_intl_annual_gbp_estimate), level: "uni" },
  ]);

  // Domestic rate — course-level first, then uni-level, then the
  // country-level fallback bundled with the fee-status catalogue.
  const homePick = pick([
    { v: usd(cost.tuition_domestic_annual_usd), level: "course" },
    { v: gbp(cost.tuition_domestic_annual_gbp), level: "course" },
    { v: usd(u.tuition_domestic_annual_usd), level: "uni" },
    { v: gbp(u.tuition_domestic_annual_gbp), level: "uni" },
    { v: engineKey ? usd(HOME_FEE_FALLBACK_USD[engineKey]) : null, level: "country" },
  ]);

  const useHome = feeStatus === "home" && homePick.v != null;
  const chosen = useHome ? homePick : intlPick;
  const perYearTuition = chosen.v;
  const tuitionIsCourse = chosen.level === "course";

  // Shared known-component reading: listed required fees once; unresolved fees / unverified extras => no comparable total.
  const known = knownAnnualCost(cost, perYearTuition, tuitionIsCourse);
  const totalDegree = known.complete ? known.annual_total_usd * durationYears : null;
  // Filter out Masters-only awards on undergrad rows (and vice-versa) so the
  // "Top scholarship" column and net-cost figure aren't inflated by an
  // ineligible award.
  const eligibleSchols = filterScholarshipsByLevel(
    d.scholarships || [], d.qualification, d.duration_years,
  );
  const topSchol = eligibleSchols
    .slice()
    .sort((a, b) => (b.value_usd || 0) - (a.value_usd || 0))[0];
  const scholCredit = eligibleSchols.reduce(
    (s, x) => s + (x.value_usd || 0) * (x.duration_years || 1), 0,
  );
  const netCost = totalDegree == null ? null : Math.max(0, totalDegree - scholCredit);
  const ib = d.entry_requirements?.ib || {};
  const alevel = d.entry_requirements?.a_level || {};
  const outcomes = d.outcomes || {};
  const intl = d.international || {};

  // IB fallback — HESA courses stash the IB requirement inside
  // `entry_requirements.qualifications_alternatives.ib` as a rendered
  // string like "36 (665 at HL)". Peel out the leading integer so we
  // have something comparable across rows.
  let typicalIb = ib.typical != null ? ib.typical : null;
  if (typicalIb == null) {
    const altIb = d.entry_requirements?.qualifications_alternatives?.ib;
    if (typeof altIb === "string") {
      const m = altIb.match(/(\d{2})/);
      if (m) typicalIb = Number(m[1]);
    } else if (typeof altIb === "number") {
      typicalIb = altIb;
    }
  }

  // Compute honesty-reasons for every field that might be blank.
  const offerRateReason = d.admissions?.offer_rate == null
    ? (isHesa ? REASON_HESA_NO_OFFER
       : country === "USA" || country === "United States" ? REASON_INSTITUTION_ONLY
       : REASON_NO_UPSTREAM)
    : null;
  const ibReason = typicalIb == null ? REASON_IB_NOT_PUBLISHED : null;
  const salaryReason = outcomes.median_starting_salary_usd == null
    ? (isHesa ? REASON_HESA_SUPPRESSED : REASON_NO_UPSTREAM)
    : null;
  const employersReason = (outcomes.top_employers || []).length === 0
    ? REASON_NO_FEEDER_DATA
    : null;
  // IELTS resolution: prefer per-course, then uni-wide policy. When
  // the uni-wide value is used we tag the display so families know
  // it's an institution-level baseline, not a course-specific ask.
  const ieltsCourse = d.english_test?.ielts_min ?? null;
  const ieltsUni = u.english_test?.ielts_min ?? null;
  const ieltsValue = ieltsCourse ?? ieltsUni ?? null;
  const ieltsIsUniWide = ieltsCourse == null && ieltsUni != null;
  const ieltsDisplay = ieltsValue == null
    ? "—"
    : (ieltsIsUniWide ? `${ieltsValue} (uni-wide)` : String(ieltsValue));
  const entranceReason = !d.entrance_test
    ? "No published entrance test — offer relies on qualifications + personal statement + interview (where flagged)."
    : null;

  return {
    university: u.name || d.university_id || "—",
    country,
    qualification: d.qualification || "—",
    duration: durationYears ? `${durationYears} yrs` : "—",
    department: d.department || d.subject_area || "—",
    offer_rate: d.admissions?.offer_rate,
    typical_a_level: alevel.typical || "—",
    typical_ib: typicalIb != null ? typicalIb : "—",
    min_maths: alevel.min_maths || ib.min_maths || "—",
    ielts_min: ieltsDisplay,
    interview: d.interview_required ? "Yes" : "No",
    entrance_test: d.entrance_test || "—",
    tuition_annual: perYearTuition != null ? format(perYearTuition) : "—",
    total_cost_num: totalDegree,
    total_cost: totalDegree != null ? format(totalDegree) : "—",
    partial_annual_reference_num: known.partial_annual_reference_usd,
    partial_annual_reference: partialAnnualReferenceNote(known, format),
    top_scholarship: topSchol
      ? `${topSchol.name} — ${format(topSchol.value_usd)}/yr`
      : "None flagged",
    net_after_schol_num: netCost,
    net_after_schol: netCost != null ? format(netCost) : "—",
    // Fee-status column: a short label for the badge + a longer reason
    // for the tooltip. Empty engineKey means we don't model this
    // country, so the row shows "Intl (default)" without a reason.
    fee_status: feeStatus,
    fee_status_label: feeStatus === "home"
      ? "Home fee"
      : (engineKey ? "Intl fee" : "Intl (default)"),
    fee_status_reason: inference?.reason
      || (engineKey ? null : "Fee status not modelled for this country yet — showing international."),
    fee_status_confidence: inference?.confidence || null,
    fee_status_authority: inference?.authority_url || null,
    fee_status_is_fallback: feeStatus === "home" && homePick.level !== "course" && homePick.level !== "uni",
    employment_rate: outcomes.employment_rate,
    median_salary: outcomes.median_starting_salary_usd
      ? format(outcomes.median_starting_salary_usd)
      : "—",
    top_employers: (outcomes.top_employers || []).slice(0, 5).join(", ") || "—",
    psw_years: intl.psw_eligible_years != null ? `${intl.psw_eligible_years} yrs` : "—",
    stem_designated: intl.stem_designated ? "Yes" : "No",
    shortage: intl.shortage_occupation ? "Yes" : "No",
    warnings: d.warnings || [],
    // Per-field honesty reasons — the Compare table renders a tiny
    // "why is this blank?" tooltip when a reason exists.
    _reasons: {
      offer_rate: offerRateReason,
      typical_ib: ibReason,
      median_salary: salaryReason,
      top_employers: employersReason,
      ielts_min: ieltsValue == null ? "The university hasn't published an English-language policy in the datasets we cover — check the uni site directly." : null,
      entrance_test: entranceReason,
      total_cost: known.incomplete_reason,
      net_after_schol: known.incomplete_reason,
    },
  };
}
